import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  ButtonGroup,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AssignmentIcon from "@mui/icons-material/Assignment";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { jsPDF } from "jspdf";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import Loader from "../../../../components/Loader";
import { formatServiceLevelSummary, getServiceLabel, getServiceLevelLabel } from "../../../../libs/labels";
import contractTemplateUrl from "../../../../legal/contractor-msa-template.md";

const FILTER_STATUSES = ["all", "submitted", "shortlisted", "matched", "signed", "active", "closed"];
const DEFAULT_MONTHLY_HOURS = (40 * 52) / 12;
const POST_MATCH_STATUSES = new Set(["matched", "signed", "active", "closed"]);

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

const normalizeRateRequest = (id, data = {}) => ({
  id,
  ...data,
  contractId: readField(data, "contract_id", ""),
  accountId: readField(data, "account_id", ""),
  clientUid: readField(data, "client_uid", ""),
  contractorUid: readField(data, "contractor_uid", ""),
  pricingModel: readField(data, "pricing_model", ""),
  requestedHourlyCents: Number(readField(data, "requested_hourly_cents", 0) || 0),
  requestedMonthlyCents: Number(readField(data, "requested_monthly_cents", 0) || 0),
  effectiveDate: readField(data, "effective_date", ""),
  effectiveFrom: readField(data, "effective_from", null),
  appliedAt: readField(data, "applied_at", null),
  createdAt: readField(data, "created_at", null),
  updatedAt: readField(data, "updated_at", null),
});

const normalizePricingHistoryRow = (id, data = {}) => ({
  id,
  ...data,
  requestId: readField(data, "request_id", ""),
  fromPriceId: readField(data, "from_price_id", ""),
  toPriceId: readField(data, "to_price_id", ""),
  fromHourlyCents: Number(readField(data, "from_hourly_cents", 0) || 0),
  toHourlyCents: Number(readField(data, "to_hourly_cents", 0) || 0),
  fromMonthlyCents: Number(readField(data, "from_monthly_cents", 0) || 0),
  toMonthlyCents: Number(readField(data, "to_monthly_cents", 0) || 0),
  effectiveDate: readField(data, "effective_date", ""),
  effectiveFrom: readField(data, "effective_from", null),
  actorUid: readField(data, "actor_uid", ""),
  actorRole: readField(data, "actor_role", ""),
  createdAt: readField(data, "created_at", null),
});

const normalizeClientInvoiceRow = (id, data = {}) => ({
  id,
  ...data,
  invoiceId: readField(data, "invoice_id", id),
  contractId: readField(data, "contract_id", ""),
  accountId: readField(data, "account_id", ""),
  customerId: readField(data, "customer_id", ""),
  priceId: readField(data, "price_id", ""),
  pricingModel: readField(data, "pricing_model", ""),
  billingMonth: readField(data, "billing_month", ""),
  supplyDate: readField(data, "supply_date", ""),
  periodStart: readField(data, "period_start", ""),
  periodEnd: readField(data, "period_end", ""),
  periodStartSec: readField(data, "period_start_sec", 0),
  periodEndSec: readField(data, "period_end_sec", 0),
  amountDueCents: readField(data, "amount_due_cents", 0),
  amountPaidCents: readField(data, "amount_paid_cents", 0),
  amountRemainingCents: readField(data, "amount_remaining_cents", 0),
  createdSec: readField(data, "created_sec", 0),
  dueDateSec: readField(data, "due_date_sec", 0),
  hostedInvoiceUrl: readField(data, "hosted_invoice_url", ""),
  invoicePdf: readField(data, "invoice_pdf", ""),
});

const normalizeStatusFilter = (value) => {
  const normalized = String(value || "all").toLowerCase();
  return FILTER_STATUSES.includes(normalized) ? normalized : "all";
};

const formatDateTime = (timestamp) => {
  const sec = timestamp?.seconds || 0;
  if (!sec) return "—";
  return new Date(sec * 1000).toLocaleString();
};

const statusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const interviewStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "completed") return "success";
  if (value === "scheduled") return "info";
  if (value === "requested" || value === "availability_sent") return "warning";
  if (value === "no_show") return "error";
  return "default";
};

const statusHistoryLabel = (item = {}) => {
  const action = String(item?.action || "").toLowerCase();
  const decision = String(item?.decision || item?.contractor_decision || "").toLowerCase();

  if (action === "contractor_invitation_response" || action === "contractor_response") {
    if (decision === "accepted") return "Accepted";
    if (decision === "declined") return "Declined";
    if (decision) return statusLabel(decision);
  }

  if (action === "candidate_feedback_updated") return "Candidate feedback updated";
  if (action === "candidates_updated") return "Shortlist updated";

  const to = String(item?.to || item?.status || "").trim();
  return to ? statusLabel(to) : statusLabel(action || "updated");
};

const toDateDisplay = (value) => {
  if (!value) return "—";
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString();
    return value;
  }
  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000).toLocaleDateString();
  }
  return "—";
};

const fromUnixSecToDate = (sec) => {
  const n = Number(sec || 0);
  if (!(n > 0)) return null;
  const dt = new Date(n * 1000);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const toIsoDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  let dt = null;
  if (typeof value?.seconds === "number") {
    dt = new Date(value.seconds * 1000);
  } else if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) dt = parsed;
  } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
    dt = value;
  }
  if (!dt) return "";
  return dt.toISOString().slice(0, 10);
};

const getFirstDayOfNextMonthString = () => {
  const now = new Date();
  const dt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getCurrentUtcMonthString = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const resolveMonthPeriod = (yearMonth) => {
  const raw = String(yearMonth || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})$/);
  if (!match) return { periodStart: "", periodEnd: "" };
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!(year > 1900) || month < 1 || month > 12) return { periodStart: "", periodEnd: "" };
  const start = new Date(Date.UTC(year, month - 1, 1));
  const end = new Date(Date.UTC(year, month, 0));
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end.toISOString().slice(0, 10),
  };
};

const DotStepIcon = () => (
  <Box
    sx={(theme) => ({
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: theme.palette.text.secondary,
      mt: 0.75,
    })}
  />
);

const formatMetadataTooltip = (metadata) => {
  if (!metadata || typeof metadata !== "object") return "";
  const entries = Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && value !== "");
  if (!entries.length) return "";
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join("\n");
};

const actionIconSx = (theme, mode = "default") => {
  const paletteMap = {
    default: {
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      bg: "transparent",
      hoverBg: theme.palette.action.hover,
      hoverBorderColor: theme.palette.text.secondary,
      hoverColor: theme.palette.text.primary,
    },
    info: {
      color: theme.palette.info.main,
      borderColor: theme.palette.info.light,
      bg: theme.palette.info.light + "22",
      hoverBg: theme.palette.info.light + "33",
      hoverBorderColor: theme.palette.info.main,
      hoverColor: theme.palette.info.dark,
    },
    secondary: {
      color: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.light,
      bg: theme.palette.secondary.light + "22",
      hoverBg: theme.palette.secondary.light + "33",
      hoverBorderColor: theme.palette.secondary.main,
      hoverColor: theme.palette.secondary.dark,
    },
    warning: {
      color: theme.palette.warning.main,
      borderColor: theme.palette.warning.light,
      bg: theme.palette.warning.light + "22",
      hoverBg: theme.palette.warning.light + "33",
      hoverBorderColor: theme.palette.warning.main,
      hoverColor: theme.palette.warning.dark,
    },
    success: {
      color: theme.palette.success.main,
      borderColor: theme.palette.success.light,
      bg: theme.palette.success.light + "22",
      hoverBg: theme.palette.success.light + "33",
      hoverBorderColor: theme.palette.success.main,
      hoverColor: theme.palette.success.dark,
    },
  };
  const p = paletteMap[mode] || paletteMap.default;
  return {
    width: 32,
    height: 32,
    border: `1px solid ${p.borderColor}`,
    borderRadius: 1,
    color: p.color,
    backgroundColor: p.bg,
    "&:hover": {
      backgroundColor: p.hoverBg,
      borderColor: p.hoverBorderColor,
      color: p.hoverColor,
    },
    "&.Mui-disabled": {
      borderColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };
};

const Contract = ({
  loading,
  contracts,
  title = "Hire Requests",
  showStatusFilter = true,
  showAssignmentFilter = false,
  defaultStatusFilter = "all",
  defaultAssignmentFilter = "all",
  contractors,
  adminUid,
  busyById,
  statusColor,
  formatCurrency,
  formatDate,
  onClaim,
  onMoveStatus,
  onApproveMatchedContract,
  onReturnMatchedContract,
  onConfirmExternalSignatures,
  onUnclaim,
  onGenerateCandidateMatches,
  onUpdateCandidates,
  onUpdateCandidate,
  onRequestRateChange,
  resolveUserLabel,
  contractorProfilesByUid,
}) => {
  const theme = useTheme();
  const hideHistoryPanel = useMediaQuery(theme.breakpoints.down("md"));
  const [requestPage, setRequestPage] = useState(0);
  const [requestRowsPerPage, setRequestRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState(() => normalizeStatusFilter(defaultStatusFilter));

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptReason, setPromptReason] = useState("");
  const [promptConfirmText, setPromptConfirmText] = useState("");
  const [promptAction, setPromptAction] = useState({ type: "", contract: null, nextStatus: "" });
  const [promptStartDate, setPromptStartDate] = useState("");
  const [promptEndDate, setPromptEndDate] = useState("");
  const [promptError, setPromptError] = useState("");
  const [promptSubmitting, setPromptSubmitting] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [selectedRequestSnapshot, setSelectedRequestSnapshot] = useState(null);
  const [requestTimelineLoading, setRequestTimelineLoading] = useState(false);
  const [requestAuditTimeline, setRequestAuditTimeline] = useState([]);
  const [pricingHistoryRows, setPricingHistoryRows] = useState([]);
  const [pricingHistoryLoading, setPricingHistoryLoading] = useState(false);
  const [upcomingRateChange, setUpcomingRateChange] = useState(null);
  const [historyMode, setHistoryMode] = useState("status");

  const [candidateReason, setCandidateReason] = useState("");
  const [selectedCandidateUids, setSelectedCandidateUids] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidatePage, setCandidatePage] = useState(0);
  const [candidateRowsPerPage, setCandidateRowsPerPage] = useState(10);
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [candidateDialogUid, setCandidateDialogUid] = useState("");
  const [candidateDialogBusy, setCandidateDialogBusy] = useState(false);
  const [candidateInterviewStatus, setCandidateInterviewStatus] = useState("not_scheduled");
  const [candidateInterviewAt, setCandidateInterviewAt] = useState("");
  const [candidateAdminFeedback, setCandidateAdminFeedback] = useState("");
  const [candidateClientFeedback, setCandidateClientFeedback] = useState("");
  const [candidateSelected, setCandidateSelected] = useState(false);
  const [contractPdfBusy, setContractPdfBusy] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateDialogBusy, setRateDialogBusy] = useState(false);
  const [rateDialogError, setRateDialogError] = useState("");
  const [rateReason, setRateReason] = useState("");
  const [rateEffectiveDate, setRateEffectiveDate] = useState(getFirstDayOfNextMonthString());
  const [requestedHourlyRate, setRequestedHourlyRate] = useState("");
  const [requestedMonthlyRate, setRequestedMonthlyRate] = useState("");
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientInvoicesLoading, setClientInvoicesLoading] = useState(false);
  const [clientInvoiceError, setClientInvoiceError] = useState("");
  const [clientInvoiceDialogOpen, setClientInvoiceDialogOpen] = useState(false);
  const [clientInvoiceDialogBusy, setClientInvoiceDialogBusy] = useState(false);
  const [clientInvoiceMonth, setClientInvoiceMonth] = useState("");
  const [clientInvoiceQuantity, setClientInvoiceQuantity] = useState("");
  const [clientInvoiceDialogError, setClientInvoiceDialogError] = useState("");
  const [clientInvoicePage, setClientInvoicePage] = useState(0);
  const [clientInvoiceRowsPerPage, setClientInvoiceRowsPerPage] = useState(5);

  useEffect(() => {
    setStatusFilter(normalizeStatusFilter(defaultStatusFilter));
    setRequestPage(0);
  }, [defaultStatusFilter]);

  useEffect(() => {
    setClientInvoicePage(0);
  }, [selectedRequestId, clientInvoices.length]);

  const resolveActorLabel = (actor, fallback = "system") => {
    const raw = String(actor || "").trim();
    if (!raw) return fallback;
    const compact = raw.includes(":") ? raw.split(":").pop() : raw;
    if (compact.includes("@")) return compact;
    return resolveUserLabel?.(compact, raw) || raw;
  };

  const tokenize = useCallback((input) =>
    Array.from(
      new Set(
        String(input || "")
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, " ")
          .split(/\s+/)
          .map((t) => t.trim())
          .filter((t) => t.length >= 3)
      )
    ), []);

  const parseLevelNumber = useCallback((levelValue) => {
    const normalized = String(levelValue || "").trim().toUpperCase();
    const match = normalized.match(/^L(\d+)$/);
    if (match) return Number(match[1]);
    const rankMap = {
      ASSOCIATE: 1,
      MID: 2,
      SENIOR: 3,
      STAFF: 4,
      PRINCIPAL: 5,
    };
    return rankMap[normalized] || null;
  }, []);

  const selectedRequest = useMemo(
    () =>
      contracts.find((row) => String(row.id || "") === String(selectedRequestId || "")) ||
      (String(selectedRequestSnapshot?.id || "") === String(selectedRequestId || "") ? selectedRequestSnapshot : null),
    [contracts, selectedRequestId, selectedRequestSnapshot]
  );

  useEffect(() => {
    if (!selectedRequestId) return;
    const fresh = contracts.find((row) => String(row.id || "") === String(selectedRequestId || ""));
    if (fresh) setSelectedRequestSnapshot(fresh);
  }, [contracts, selectedRequestId]);
  const canManageSelected = Boolean(selectedRequest);
  const selectedStatusKey = String(selectedRequest?.status || "").toLowerCase();
  const selectedPricingModel = String(selectedRequest?.pricingModel || "").toLowerCase();
  const selectedContractorLabel = String(
    selectedRequest?.contractor?.displayName ||
    selectedRequest?.contractor?.name ||
    "Contractor"
  );
  const selectedContractRateCents = selectedPricingModel === "fixed_monthly"
    ? Number(selectedRequest?.monthlyBudgetCents || 0)
    : Number(selectedRequest?.hourlyBudgetCents || 0);
  const selectedInvoiceQuantity = selectedPricingModel === "fixed_monthly"
    ? 1
    : Number(clientInvoiceQuantity || 0);
  const selectedInvoiceTotalCents = selectedPricingModel === "fixed_monthly"
    ? selectedContractRateCents
    : (Number.isInteger(selectedInvoiceQuantity) && selectedInvoiceQuantity > 0
      ? selectedContractRateCents * selectedInvoiceQuantity
      : 0);
  const selectedContractPriceLabel = selectedPricingModel === "fixed_monthly"
    ? `${formatCurrency(selectedContractRateCents, selectedRequest?.currency || "usd")}/mo`
    : `${formatCurrency(selectedContractRateCents, selectedRequest?.currency || "usd")}/hr`;
  const selectedInvoiceTotalHelper = selectedPricingModel === "fixed_monthly"
    ? `Calculation: ${formatCurrency(selectedContractRateCents, selectedRequest?.currency || "usd")} monthly fixed price.`
    : `Calculation: ${selectedInvoiceQuantity > 0 ? selectedInvoiceQuantity : 0} hours × ${formatCurrency(selectedContractRateCents, selectedRequest?.currency || "usd")} per hour.`;
  const resolvedInvoicePeriod = useMemo(
    () => resolveMonthPeriod(clientInvoiceMonth),
    [clientInvoiceMonth]
  );
  const isPostMatchSelected = POST_MATCH_STATUSES.has(selectedStatusKey);
  const canRequestRateChange = canManageSelected && selectedStatusKey === "active";

  const getContractorHourlyBase = useCallback((contractor) => {
    const hourlyCents = Number(contractor?.metadata?.price || contractor?.hourlyCents || 0);
    return hourlyCents;
  }, []);

  const getClientHourlyFromRequest = useCallback((request) => {
    const model = String(request?.pricingModel || "").toLowerCase();
    if (model === "fixed_monthly") {
      const monthly = Number(request?.monthlyBudgetCents || 0);
      return monthly > 0 ? Math.round(monthly / DEFAULT_MONTHLY_HOURS) : 0;
    }
    return Number(request?.hourlyBudgetCents || 0);
  }, []);

  const getProfitability = useCallback((request, contractor) => {
    const model = String(request?.pricingModel || "").toLowerCase();
    const clientHourly = getClientHourlyFromRequest(request);
    const contractorHourly = getContractorHourlyBase(contractor);

    const clientMonthly = model === "fixed_monthly"
      ? Number(request?.monthlyBudgetCents || 0)
      : Math.round(clientHourly * DEFAULT_MONTHLY_HOURS);
    const contractorMonthly = Math.round(contractorHourly * DEFAULT_MONTHLY_HOURS);

    const margin = clientMonthly - contractorMonthly;
    const marginPct = clientMonthly > 0 ? (margin / clientMonthly) * 100 : 0;
    return { clientMonthly, contractorMonthly, margin, marginPct };
  }, [getClientHourlyFromRequest, getContractorHourlyBase]);

  const getMatchScore = useCallback((request, contractor, profile) => {
    if (!request || !contractor || !profile) return 0;

    const requestedService = String(request.roleCode || "").toLowerCase();
    const candidateService = String(contractor?.metadata?.service || profile?.service || "").toLowerCase();
    const serviceScore = requestedService && candidateService
      ? (requestedService === candidateService ? 35 : 0)
      : 0;

    const requestedLevel = parseLevelNumber(request.roleLevel);
    const candidateLevel = parseLevelNumber(contractor?.metadata?.level || profile?.level);
    let levelScore = 0;
    if (requestedLevel && candidateLevel) {
      const diff = Math.abs(requestedLevel - candidateLevel);
      if (diff === 0) levelScore = 25;
      else if (diff === 1) levelScore = 15;
      else if (diff === 2) levelScore = 8;
    }

    const reqTokens = tokenize(`${request.requirements || ""} ${request.roleTitle || ""} ${request.roleLabel || ""}`);
    const profileText = [
      profile.generatedResume || "",
      profile.summary || "",
      ...(Array.isArray(profile.skills) ? profile.skills : []),
      ...(Array.isArray(profile.domainFocus) ? profile.domainFocus : []),
      ...(Array.isArray(profile.experienceSummary) ? profile.experienceSummary : []),
    ].join(" ");
    const profileTokens = new Set(tokenize(profileText));
    const overlap = reqTokens.filter((token) => profileTokens.has(token)).length;
    const keywordRatio = reqTokens.length > 0 ? overlap / reqTokens.length : 0;
    const keywordScore = Math.min(40, Math.round(keywordRatio * 40));

    return Math.max(0, Math.min(100, serviceScore + levelScore + keywordScore));
  }, [parseLevelNumber, tokenize]);

  const saveShortlist = async () => {
    if (!selectedRequest || typeof onUpdateCandidates !== "function") return;
    await onUpdateCandidates(selectedRequest, selectedCandidateUids, candidateReason);
    setCandidateReason("");
  };

  const generateMatches = async () => {
    if (!selectedRequest || typeof onGenerateCandidateMatches !== "function") return;
    await onGenerateCandidateMatches(selectedRequest);
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter((c) => {
      const status = String(c.status || "").toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      return true;
    });
  }, [contracts, statusFilter]);

  const pagedContracts = useMemo(
    () =>
      filteredContracts.slice(
        requestPage * requestRowsPerPage,
        requestPage * requestRowsPerPage + requestRowsPerPage
      ),
    [filteredContracts, requestPage, requestRowsPerPage]
  );

  const profileBackedCandidates = useMemo(() => {
    if (!selectedRequest) return [];
    const savedCandidateMap = new Map(
      (Array.isArray(selectedRequest.candidateContractors) ? selectedRequest.candidateContractors : [])
        .map((candidate) => [String(candidate?.uid || ""), candidate])
        .filter(([uid]) => !!uid)
    );

    return contractors
      .map((u) => {
        const uid = String(u.uid || "").trim();
        const profile = contractorProfilesByUid?.[uid];
        if (!uid || !profile || !savedCandidateMap.has(uid)) return null;
        const savedCandidate = savedCandidateMap.get(uid) || {};
        const aiScore = Number(savedCandidate.matchScore || 0);
        const score = aiScore > 0 ? aiScore : getMatchScore(selectedRequest, u, profile);
        const profitability = getProfitability(selectedRequest, u);
        const firstName = String(u.fname || "").trim();
        const lastName = String(u.lname || "").trim();
        const email = String(u.email || "").trim();
        const displayName = `${firstName} ${lastName}`.trim() || String(u.displayName || "").trim() || "Contractor";
        return {
          uid,
          contractor: u,
          profile,
          score,
          aiMatch: aiScore > 0,
          profitabilityScore: Number(savedCandidate.profitabilityScore || 0),
          matchFeedback: String(savedCandidate.matchFeedback || ""),
          matchStrengths: Array.isArray(savedCandidate.matchStrengths) ? savedCandidate.matchStrengths : [],
          matchGaps: Array.isArray(savedCandidate.matchGaps) ? savedCandidate.matchGaps : [],
          profitability,
          firstName,
          lastName,
          email,
          displayName,
          service: getServiceLabel(u?.metadata?.service),
          serviceKey: String(u?.metadata?.service || "").toLowerCase(),
          level: getServiceLevelLabel(u?.metadata?.service, u?.metadata?.level),
          rateLabel: `${formatCurrency(Number(u?.metadata?.price || 0), u?.metadata?.currency || selectedRequest.currency || "usd")}/hr`,
        };
      })
      .filter(Boolean);
  }, [contractors, contractorProfilesByUid, selectedRequest, formatCurrency, getMatchScore, getProfitability]);

  const filteredCandidates = useMemo(() => {
    const q = String(candidateSearch || "").trim().toLowerCase();
    const rows = profileBackedCandidates.filter((row) => {
      if (!q) return true;
      return [
        row.firstName,
        row.lastName,
        row.displayName,
        row.email,
        row.serviceKey,
        row.service,
        row.level,
        row.contractor?.metadata?.category || "",
        row.contractor?.metadata?.sub_category || "",
        row.profile?.summary || "",
        row.profile?.generatedResume || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });

    const shortlistSet = new Set(selectedCandidateUids);
    rows.sort((a, b) => {
      const aPinned = shortlistSet.has(a.uid) ? 1 : 0;
      const bPinned = shortlistSet.has(b.uid) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      if (a.score !== b.score) return b.score - a.score;
      return a.displayName.localeCompare(b.displayName);
    });

    return rows;
  }, [profileBackedCandidates, candidateSearch, selectedCandidateUids]);

  const pagedCandidates = useMemo(() => {
    const start = candidatePage * candidateRowsPerPage;
    return filteredCandidates.slice(start, start + candidateRowsPerPage);
  }, [filteredCandidates, candidatePage, candidateRowsPerPage]);

  const candidateDetailsMap = useMemo(() => {
    const rows = Array.isArray(selectedRequest?.candidateContractors) ? selectedRequest.candidateContractors : [];
    return rows.reduce((acc, row) => {
      const uid = String(row?.uid || "").trim();
      if (uid) acc[uid] = row || {};
      return acc;
    }, {});
  }, [selectedRequest]);

  const matchedCandidate = useMemo(() => {
    if (!selectedRequest) return null;
    const rows = Array.isArray(selectedRequest.candidateContractors) ? selectedRequest.candidateContractors : [];
    const selectedAccepted = rows.find(
      (c) => !!c?.selected && String(c?.contractorDecision || "").toLowerCase() === "accepted"
    );
    const linked = rows.find(
      (c) => String(c?.uid || "") === String(selectedRequest.contractorUid || selectedRequest.matchedContractorUid || "")
    );
    return selectedAccepted || linked || null;
  }, [selectedRequest]);

  const handleDownloadCandidateContractPdf = async () => {
    if (!selectedRequest || !matchedCandidate) return;
    if (contractPdfBusy) return;

    setContractPdfBusy(true);
    try {
      const requestId = String(selectedRequest.id || "").trim();
      const candidateName =
        String(matchedCandidate.displayName || "").trim() ||
        "Contractor";
      const effectiveDateRaw =
        selectedRequest.contractStartAt ||
        selectedRequest.startDate ||
        selectedRequest.createdAt ||
        null;
      const endDateRaw =
        selectedRequest.contractEndAt ||
        selectedRequest.endDate ||
        null;
      const requirements = String(selectedRequest.requirements || "").trim() || "As agreed in the active request scope.";
      const currency = String(selectedRequest.currency || "usd").toUpperCase();
      const contractorHourlyCents = Number(
        matchedCandidate?.hourlyCents ||
        matchedCandidate?.metadata?.price ||
        selectedRequest?.contractor?.hourlyCents ||
        selectedRequest?.contractor?.metadata?.price ||
        0
      );
      const hourlyRateLabel = `${formatCurrency(contractorHourlyCents, selectedRequest.currency || "usd")} / hour`;
      const authDisplayName = String(FirebaseAuth.auth().currentUser?.displayName || "").trim();
      const resolvedAdminLabel = String(resolveUserLabel?.(adminUid, "") || "").trim();
      const adminName = authDisplayName || resolvedAdminLabel || "Admin";
      const contractorUid = String(
        matchedCandidate?.uid ||
        selectedRequest?.contractorUid ||
        selectedRequest?.matchedContractorUid ||
        ""
      ).trim();
      let contractorAddress = "___________________________";

      let resolvedClientName = String(
        selectedRequest.accountName ||
        selectedRequest.companyName ||
        ""
      ).trim();

      if (!resolvedClientName || resolvedClientName.toLowerCase() === "client") {
        const accountId = String(selectedRequest.accountId || "").trim();
        if (accountId) {
          try {
            const accountSnap = await FirebaseAuth.firestore().collection("accounts").doc(accountId).get();
            if (accountSnap.exists) {
              const accountData = accountSnap.data() || {};
              const accountName = String(accountData.company_name || accountData.name || "").trim();
              if (accountName) {
                resolvedClientName = accountName;
              }
            }
          } catch (_error) {
            // Keep best-effort fallback behavior.
          }
        }
      }

      if (!resolvedClientName) {
        resolvedClientName = "—";
      }

      if (contractorUid) {
        try {
          const contractorSnap = await FirebaseAuth.firestore().collection("users").doc(contractorUid).get();
          if (contractorSnap.exists) {
            const contractorData = contractorSnap.data() || {};
            const addr = contractorData?.individual?.address || {};
            const parts = [
              String(addr.line1 || "").trim(),
              String(addr.line2 || "").trim(),
              String(addr.city || "").trim(),
              String(addr.state || "").trim(),
              String(addr.postal_code || "").trim(),
              String(addr.country || "").trim(),
            ].filter(Boolean);
            if (parts.length > 0) {
              contractorAddress = parts.join(", ");
            }
          }
        } catch (_error) {
          // Best-effort only; keep placeholder fallback.
        }
      }

      const response = await fetch(contractTemplateUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Failed to load contract template (${response.status})`);
      }
      const templateRaw = await response.text();

      const values = {
        Effective_Date: toDateDisplay(effectiveDateRaw),
        Start_Date: toDateDisplay(effectiveDateRaw),
        End_Date_or_NA: toDateDisplay(endDateRaw),
        Contractor_Full_Name: candidateName,
        Contractor_Address: contractorAddress,
        Contract_ID: requestId || "—",
        Client_Name: resolvedClientName,
        Requirements: requirements,
        Currency: currency,
        Hourly_Rate: hourlyRateLabel,
        Ops51_Admin_Name: adminName,
        Ops51_Admin_Title: "___________________________",
      };

      const contractMarkdown = templateRaw.replace(/\{\{([^}]+)\}\}/g, (_match, key) => {
        const safeKey = String(key || "").trim();
        return Object.prototype.hasOwnProperty.call(values, safeKey) ? String(values[safeKey]) : "";
      });

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      doc.setProperties({
        title: `Ops51 Service Agreement - ${requestId || "Draft"}`,
        subject: "Independent Contractor Agreement",
        author: "Ops51 Technologies Inc.",
        creator: "Ops51 Platform",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginLeft = 56;
      const marginRight = 56;
      const marginTop = 64;
      const marginBottom = 62;
      const contentWidth = pageWidth - marginLeft - marginRight;
      let y = marginTop;

      const addPage = () => {
        doc.addPage();
        y = marginTop;
      };

      const ensureSpace = (heightNeeded = 14) => {
        if (y + heightNeeded > pageHeight - marginBottom) {
          addPage();
        }
      };

      const cleanInlineMarkdown = (text) =>
        String(text || "")
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .replace(/`([^`]+)`/g, "$1")
          .replace(/\s+/g, " ")
          .trim();

      const writeWrapped = (text, options = {}) => {
        const {
          size = 12,
          style = "normal",
          gapAfter = 6,
          indent = 0,
          lineHeight = size + 3.2,
          align = "left",
        } = options;
        const x = align === "center" ? pageWidth / 2 : marginLeft + indent;
        const width = contentWidth - indent;
        doc.setFont("times", style);
        doc.setFontSize(size);
        const rows = doc.splitTextToSize(cleanInlineMarkdown(text), width);
        const blockHeight = rows.length * lineHeight;
        ensureSpace(blockHeight + gapAfter);
        if (align === "center") {
          doc.text(rows, x, y, { align: "center" });
        } else {
          doc.text(rows, x, y);
        }
        y += blockHeight + gapAfter;
      };

      const writeCodeBlock = (codeText) => {
        const codeRaw = String(codeText || "");
        if (!codeRaw.trim()) return;
        const codeX = marginLeft + 10;
        const codeWidth = contentWidth - 20;
        const innerPadding = 8;
        const textWidth = codeWidth - innerPadding * 2;
        const codeLineHeight = 13;

        doc.setFont("courier", "normal");
        doc.setFontSize(10.5);

        const codeRows = [];
        codeRaw.split(/\r?\n/).forEach((line) => {
          const wrapped = doc.splitTextToSize(line || " ", textWidth);
          if (Array.isArray(wrapped) && wrapped.length) {
            wrapped.forEach((row) => codeRows.push(row));
          } else {
            codeRows.push(" ");
          }
        });

        const blockHeight = codeRows.length * codeLineHeight + innerPadding * 2;
        ensureSpace(blockHeight + 8);

        doc.setDrawColor(198);
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(codeX, y, codeWidth, blockHeight, 4, 4, "FD");

        doc.setTextColor(40);
        doc.text(codeRows, codeX + innerPadding, y + innerPadding + 9);
        doc.setTextColor(0);
        y += blockHeight + 10;
      };

      const lines = contractMarkdown.split(/\r?\n/);
      let inCodeBlock = false;
      const codeLines = [];
      let blankStreak = 0;
      let centeredIntroHeadingCount = 0;
      lines.forEach((line) => {
        const raw = String(line || "");
        const trimmed = raw.trim();

        if (trimmed.startsWith("```")) {
          if (!inCodeBlock) {
            inCodeBlock = true;
            codeLines.length = 0;
          } else {
            writeCodeBlock(codeLines.join("\n"));
            inCodeBlock = false;
            codeLines.length = 0;
          }
          return;
        }

        if (inCodeBlock) {
          codeLines.push(raw);
          return;
        }

        if (!trimmed) {
          blankStreak += 1;
          y += blankStreak > 1 ? 4 : 8;
          return;
        }
        blankStreak = 0;

        if (/^---+$/.test(trimmed)) {
          ensureSpace(18);
          doc.setDrawColor(170);
          doc.setLineWidth(0.8);
          doc.line(marginLeft, y, pageWidth - marginRight, y);
          y += 12;
          return;
        }

        if (trimmed.startsWith("# ")) {
          const isIntroHeading = centeredIntroHeadingCount < 2;
          writeWrapped(trimmed.replace(/^#\s+/, ""), {
            size: 16,
            style: "bold",
            gapAfter: 10,
            align: isIntroHeading ? "center" : "left",
          });
          if (isIntroHeading) centeredIntroHeadingCount += 1;
          return;
        }

        if (trimmed.startsWith("## ")) {
          const headingText = trimmed.replace(/^##\s+/, "");
          const isIntroHeading = centeredIntroHeadingCount < 2 && /^Independent Contractor Agreement$/i.test(headingText);
          writeWrapped(headingText, {
            size: 13,
            style: "bold",
            gapAfter: 8,
            align: isIntroHeading ? "center" : "left",
          });
          if (isIntroHeading) centeredIntroHeadingCount += 1;
          return;
        }

        if (trimmed.startsWith("### ")) {
          writeWrapped(trimmed.replace(/^###\s+/, ""), { size: 12.5, style: "bold", gapAfter: 7, indent: 2 });
          return;
        }

        const bulletMatch = trimmed.match(/^-\s+(.*)$/) || raw.match(/^\s+-\s+(.*)$/);
        if (bulletMatch) {
          writeWrapped(`• ${bulletMatch[1]}`, { size: 12, style: "normal", gapAfter: 4, indent: 14 });
          return;
        }

        if (/^\d+\.\d+\s+/.test(trimmed)) {
          writeWrapped(trimmed, { size: 12, style: "normal", gapAfter: 5 });
          return;
        }

        if (/^\d+\.\s+/.test(trimmed)) {
          writeWrapped(trimmed, { size: 12.5, style: "bold", gapAfter: 6 });
          return;
        }

        if (/\*\*"[^"]+"\*\*/.test(trimmed)) {
          writeWrapped(trimmed, { size: 12, style: "bold", gapAfter: 6 });
          return;
        }

        if (/^\*\*(.+)\*\*$/.test(trimmed)) {
          writeWrapped(trimmed.replace(/^\*\*|\*\*$/g, ""), { size: 11.5, style: "bold", gapAfter: 5 });
          return;
        }

        writeWrapped(trimmed, { size: 12, style: "normal", gapAfter: 6 });
      });

      if (inCodeBlock && codeLines.length > 0) {
        writeCodeBlock(codeLines.join("\n"));
      }

      const safeId = requestId || "request";
      doc.save(`ops51_contractor_contract_${safeId}.pdf`);
    } catch (error) {
      console.error("Failed to generate contract PDF from template:", error);
    } finally {
      setContractPdfBusy(false);
    }
  };

  const toLocalDateTimeInput = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 16);
      return "";
    }
    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toISOString().slice(0, 16);
    }
    return "";
  };

  const openCandidateDialog = (uid) => {
    const detail = candidateDetailsMap[String(uid || "")] || {};
    setCandidateDialogUid(String(uid || ""));
    setCandidateInterviewStatus(String(detail.interviewStatus || "not_scheduled"));
    setCandidateInterviewAt(toLocalDateTimeInput(detail.interviewAt));
    setCandidateAdminFeedback(String(detail.adminFeedback || ""));
    setCandidateClientFeedback(String(detail.clientFeedback || ""));
    setCandidateSelected(Boolean(detail.selected));
    setCandidateDialogOpen(true);
  };

  const saveCandidateDetails = async () => {
    if (!selectedRequest || !candidateDialogUid || typeof onUpdateCandidate !== "function") return;
    const requestStatus = String(selectedRequest?.status || "").toLowerCase();
    const feedbackOnlyStatuses = ["matched", "signed", "active", "closed"];
    const feedbackOnly = feedbackOnlyStatuses.includes(requestStatus);
    setCandidateDialogBusy(true);
    try {
      const payload = {
        adminFeedback: candidateAdminFeedback,
        clientFeedback: candidateClientFeedback,
      };
      if (!feedbackOnly) {
        payload.interviewStatus = candidateInterviewStatus;
        payload.interviewAt = candidateInterviewAt ? new Date(candidateInterviewAt).toISOString() : null;
        payload.selected = candidateSelected;
      }
      await onUpdateCandidate(selectedRequest, candidateDialogUid, payload);
      setCandidateDialogOpen(false);
      setCandidateDialogUid("");
    } finally {
      setCandidateDialogBusy(false);
    }
  };

  useEffect(() => {
    setCandidatePage(0);
  }, [candidateSearch, candidateRowsPerPage, selectedRequestId]);

  const toDateInputValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    }
    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toISOString().slice(0, 10);
    }
    if (typeof value?.toDate === "function") {
      const d = value.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return "";
  };

  const formatPromptDate = (value) => {
    if (!value) return "No end date set";
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString();
      return "No end date set";
    }
    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    if (typeof value?.toDate === "function") {
      const dt = value.toDate();
      if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt.toLocaleDateString();
    }
    return "No end date set";
  };

  const openReasonPrompt = (type, contract, nextStatus = "") => {
    setPromptAction({ type, contract, nextStatus });
    setPromptReason("");
    setPromptConfirmText("");
    setPromptError("");
    if (type === "status" && nextStatus === "active") {
      setPromptStartDate(
        toDateInputValue(contract?.contractStartAt || contract?.startDate)
      );
      setPromptEndDate(
        toDateInputValue(contract?.contractEndAt || contract?.endDate)
      );
    } else {
      setPromptStartDate("");
      setPromptEndDate("");
    }
    setPromptOpen(true);
  };

  const closeReasonPrompt = (force = false) => {
    if (promptSubmitting && !force) return;
    setPromptOpen(false);
    setPromptReason("");
    setPromptConfirmText("");
    setPromptStartDate("");
    setPromptEndDate("");
    setPromptError("");
    setPromptAction({ type: "", contract: null, nextStatus: "" });
  };

  const submitReasonPrompt = async () => {
    if (promptSubmitting) return;
    const contract = promptAction.contract;
    if (!contract) return;
    setPromptError("");
    if (promptAction.type === "status") {
      if (promptAction.nextStatus === "active") {
        if (!promptStartDate) {
          setPromptError("Start date is required to move request to active.");
          return;
        }
        if (promptEndDate && promptEndDate < promptStartDate) {
          setPromptError("End date must be on or after start date.");
          return;
        }
      }
      if (promptAction.nextStatus === "closed" && String(promptConfirmText || "").trim().toLowerCase() !== "delete") {
        setPromptError('Type "delete" to confirm closing this request.');
        return;
      }
      setPromptSubmitting(true);
      await onMoveStatus(contract, promptAction.nextStatus, promptReason, {
        contractStartDate: promptStartDate || "",
        contractEndDate: promptEndDate || "",
      });
    } else if (promptAction.type === "return") {
      if (String(promptReason || "").trim().length < 8) {
        setPromptError("Provide a return reason of at least 8 characters.");
        return;
      }
      setPromptSubmitting(true);
      await onReturnMatchedContract?.(contract, promptReason);
    }
    closeReasonPrompt(true);
  };

  useEffect(() => {
    if (!promptOpen) {
      setPromptSubmitting(false);
    }
  }, [promptOpen]);

  const promptBusy = !!promptSubmitting || !!busyById[promptAction?.contract?.id];

  const submitPromptWithGuard = async () => {
    try {
      await submitReasonPrompt();
    } finally {
      setPromptSubmitting(false);
    }
  };

  const openRequestDetails = async (contract) => {
    const contractId = String(contract?.id || "");
    setSelectedRequestId(contractId);
    setSelectedRequestSnapshot(contract || null);
    setSelectedCandidateUids(
      Array.isArray(contract?.candidateContractors)
        ? contract.candidateContractors.map((c) => String(c.uid || "")).filter(Boolean)
        : []
    );
    setCandidateReason("");
    setCandidateSearch("");
    setCandidatePage(0);
    setHistoryMode("status");
    setDetailsOpen(true);
    setClientInvoiceError("");
    setClientInvoices([]);
    setRequestTimelineLoading(true);
    setRequestAuditTimeline([]);
    setPricingHistoryRows([]);
    setPricingHistoryLoading(true);
    setUpcomingRateChange(null);
    if (contractId) {
      loadClientInvoices(contractId);
    }
    try {
      const db = FirebaseAuth.firestore();
      const pricingHistoryPromise = (async () => {
        const base = db.collection("contracts").doc(contract.id).collection("pricing_history");
        const mapAppliedRequestsToHistory = (docs = []) =>
          docs
            .map((d) => normalizeRateRequest(`req-${d.id}`, d.data() || {}))
            .filter((row) => String(row.type || "").toLowerCase() === "contract_rate_change" && String(row.status || "").toLowerCase() === "applied")
            .map((row) => ({
              id: row.id,
              type: "billing_rate_change_applied",
              requestId: String(row.id || "").replace(/^req-/, ""),
              toHourlyCents: Number(row.requestedHourlyCents || 0),
              toMonthlyCents: Number(row.requestedMonthlyCents || 0),
              effectiveDate: String(row.effectiveDate || ""),
              effectiveFrom: row.effectiveFrom || null,
              createdAt: row.appliedAt || row.updatedAt || row.createdAt || null,
              source: "admin_request_fallback",
            }));
        try {
          const snap = await base.orderBy("created_at", "desc").limit(50).get();
          const rows = snap.docs.map((d) => normalizePricingHistoryRow(d.id, d.data() || {}));
          if (rows.length > 0) return rows;
          const snakeReqSnap = await db.collection("admin_requests").where("contract_id", "==", contract.id).limit(100).get();
          const docsById = new Map();
          snakeReqSnap.docs.forEach((doc) => docsById.set(doc.id, doc));
          const mapped = mapAppliedRequestsToHistory(Array.from(docsById.values()));
          mapped.sort((a, b) => Number(b.createdAt?.seconds || b.createdAt?._seconds || 0) - Number(a.createdAt?.seconds || a.createdAt?._seconds || 0));
          return mapped;
        } catch (_error) {
          const snap = await base.limit(50).get();
          const rows = snap.docs.map((d) => normalizePricingHistoryRow(d.id, d.data() || {}));
          rows.sort((a, b) => Number(b.createdAt?.seconds || b.createdAt?._seconds || 0) - Number(a.createdAt?.seconds || a.createdAt?._seconds || 0));
          if (rows.length > 0) return rows;
          const snakeReqSnap = await db.collection("admin_requests").where("contract_id", "==", contract.id).limit(100).get();
          const docsById = new Map();
          snakeReqSnap.docs.forEach((doc) => docsById.set(doc.id, doc));
          const mapped = mapAppliedRequestsToHistory(Array.from(docsById.values()));
          mapped.sort((a, b) => Number(b.createdAt?.seconds || b.createdAt?._seconds || 0) - Number(a.createdAt?.seconds || a.createdAt?._seconds || 0));
          return mapped;
        }
      })();
      const upcomingRateChangePromise = (async () => {
        const snakeSnap = await db
          .collection("admin_requests")
          .where("type", "==", "contract_rate_change")
          .where("contract_id", "==", contract.id)
          .where("status", "==", "approved")
          .limit(50)
          .get();
        const docsById = new Map();
        snakeSnap.docs.forEach((doc) => docsById.set(doc.id, doc));
        const rows = Array.from(docsById.values())
          .map((d) => normalizeRateRequest(d.id, d.data() || {}))
          .filter((row) => Number(row?.effectiveFrom?.seconds || row?.effectiveFrom?._seconds || 0) > 0)
          .sort((a, b) => {
            const aSec = Number(a?.effectiveFrom?.seconds || a?.effectiveFrom?._seconds || 0);
            const bSec = Number(b?.effectiveFrom?.seconds || b?.effectiveFrom?._seconds || 0);
            return aSec - bSec;
          });
        return rows[0] || null;
      })();
      const scopedQueries = [
        db.collection("admin_activity").where("entity_id", "==", contract.id).limit(100).get(),
      ];
      const queryResults = await Promise.allSettled(scopedQueries);

      const mergedById = new Map();
      queryResults.forEach((result, index) => {
        if (result.status !== "fulfilled") return;
        result.value.docs.forEach((d) => {
          const data = d.data() || {};
          mergedById.set(d.ref.path, {
            id: `audit-${d.id}`,
            ...data,
            adminUid: readField(data, "admin_uid", ""),
            entityId: readField(data, "entity_id", ""),
            createdAt: readField(data, "created_at", null),
          });
        });
      });
      const merged = Array.from(mergedById.values());
      const rows = merged
        .filter((r) => String(r.entity || "").toLowerCase() === "contracts" || !String(r.entity || "").trim())
        .map((r) => ({ ...r, createdAt: r.createdAt || null }));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setRequestAuditTimeline(rows);
      const [pricingResult, upcomingResult] = await Promise.allSettled([
        pricingHistoryPromise,
        upcomingRateChangePromise,
      ]);
      setPricingHistoryRows(pricingResult.status === "fulfilled" ? pricingResult.value : []);
      setUpcomingRateChange(upcomingResult.status === "fulfilled" ? upcomingResult.value : null);
    } finally {
      setRequestTimelineLoading(false);
      setPricingHistoryLoading(false);
    }
  };

  const openRateDialog = () => {
    if (!selectedRequest) return;
    setRateDialogError("");
    setRateReason("");
    setRateEffectiveDate(getFirstDayOfNextMonthString());
    const currentHourly = Number(selectedRequest?.hourlyBudgetCents || 0) / 100;
    const currentMonthly = Number(selectedRequest?.monthlyBudgetCents || 0) / 100;
    setRequestedHourlyRate(currentHourly > 0 ? currentHourly.toFixed(2) : "");
    setRequestedMonthlyRate(currentMonthly > 0 ? currentMonthly.toFixed(2) : "");
    setRateDialogOpen(true);
  };

  const closeRateDialog = () => {
    if (rateDialogBusy) return;
    setRateDialogOpen(false);
    setRateDialogError("");
  };

  const submitRateChangeRequest = async () => {
    if (!selectedRequest || typeof onRequestRateChange !== "function") return;
    const requestedHourly = Number(requestedHourlyRate || 0);
    const requestedMonthly = Number(requestedMonthlyRate || 0);
    const targetRate = selectedPricingModel === "fixed_monthly" ? requestedMonthly : requestedHourly;
    if (!(targetRate > 0)) {
      setRateDialogError(selectedPricingModel === "fixed_monthly" ? "Enter a valid monthly rate." : "Enter a valid hourly rate.");
      return;
    }
    if (!rateEffectiveDate) {
      setRateDialogError("Effective date is required.");
      return;
    }
    if (!String(rateEffectiveDate).endsWith("-01")) {
      setRateDialogError("Effective date must be the first day of a month.");
      return;
    }
    if (String(rateEffectiveDate) < getFirstDayOfNextMonthString()) {
      setRateDialogError(`Effective date must be ${getFirstDayOfNextMonthString()} or later.`);
      return;
    }
    setRateDialogBusy(true);
    setRateDialogError("");
    try {
      await onRequestRateChange(selectedRequest, {
        requestedHourlyRate: requestedHourly,
        requestedMonthlyRate: requestedMonthly,
        effectiveDate: rateEffectiveDate,
        reason: rateReason,
      });
      setRateDialogOpen(false);
    } catch (_error) {
      setRateDialogError("Failed to submit rate-change request.");
    } finally {
      setRateDialogBusy(false);
    }
  };

  const loadClientInvoices = async (contractId) => {
    const id = String(contractId || "").trim();
    if (!id) {
      setClientInvoices([]);
      return;
    }
    setClientInvoicesLoading(true);
    setClientInvoiceError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminListContractClientInvoices");
      const res = await fn({ contract_id: id });
      const rows = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
      setClientInvoices(rows.map((row) => normalizeClientInvoiceRow(row?.id || row?.invoice_id || "", row)));
    } catch (error) {
      setClientInvoiceError(String(error?.message || "Failed to load client invoices."));
      setClientInvoices([]);
    } finally {
      setClientInvoicesLoading(false);
    }
  };

  const openClientInvoiceDialog = () => {
    if (!selectedRequest?.id) return;
    const start = toIsoDateInput(selectedRequest.contractStartAt || selectedRequest.startDate);
    const startMonth = /^\d{4}-\d{2}-\d{2}$/.test(start) ? start.slice(0, 7) : "";
    setClientInvoiceMonth(startMonth || getCurrentUtcMonthString());
    setClientInvoiceQuantity(selectedPricingModel === "fixed_monthly" ? "1" : "");
    setClientInvoiceDialogError("");
    setClientInvoiceDialogOpen(true);
  };

  const closeClientInvoiceDialog = () => {
    if (clientInvoiceDialogBusy) return;
    setClientInvoiceDialogOpen(false);
    setClientInvoiceDialogError("");
  };

  const submitClientInvoice = async () => {
    if (!selectedRequest?.id) return;
    if (!clientInvoiceMonth) {
      setClientInvoiceDialogError("Billing month is required.");
      return;
    }
    const periodStart = String(resolvedInvoicePeriod.periodStart || "");
    const periodEnd = String(resolvedInvoicePeriod.periodEnd || "");
    if (!periodStart || !periodEnd) {
      setClientInvoiceDialogError("Unable to resolve period start and end from billing month.");
      return;
    }
    if (selectedPricingModel !== "fixed_monthly") {
      const qty = Number(clientInvoiceQuantity || 0);
      if (!(qty > 0) || !Number.isInteger(qty)) {
        setClientInvoiceDialogError("Billable hours must be a positive whole number.");
        return;
      }
    }
    setClientInvoiceDialogBusy(true);
    setClientInvoiceDialogError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminCreateContractClientInvoice");
      await fn({
        contract_id: selectedRequest.id,
        billing_month: clientInvoiceMonth,
        period_start: periodStart,
        period_end: periodEnd,
        quantity: selectedPricingModel === "fixed_monthly" ? 1 : Number(clientInvoiceQuantity || 0),
      });
      setClientInvoiceDialogOpen(false);
      await loadClientInvoices(selectedRequest.id);
    } catch (error) {
      setClientInvoiceDialogError(String(error?.message || "Failed to create invoice."));
    } finally {
      setClientInvoiceDialogBusy(false);
    }
  };

  const sendClientInvoice = async (invoiceId) => {
    const id = String(invoiceId || "").trim();
    if (!selectedRequest?.id || !id) return;
    setClientInvoicesLoading(true);
    setClientInvoiceError("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminSendContractClientInvoice");
      await fn({ contract_id: selectedRequest.id, invoice_id: id });
      await loadClientInvoices(selectedRequest.id);
    } catch (error) {
      setClientInvoiceError(String(error?.message || "Failed to send invoice."));
    } finally {
      setClientInvoicesLoading(false);
    }
  };

  const pagedClientInvoices = useMemo(() => {
    return clientInvoices.slice(
      clientInvoicePage * clientInvoiceRowsPerPage,
      clientInvoicePage * clientInvoiceRowsPerPage + clientInvoiceRowsPerPage
    );
  }, [clientInvoices, clientInvoicePage, clientInvoiceRowsPerPage]);

  const formatInvoiceDate = (sec) => {
    const dt = fromUnixSecToDate(sec);
    if (!dt) return "—";
    return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  };

  const formatSupplyMonth = (invoice) => {
    const raw = String(invoice?.billingMonth || "").trim();
    if (/^\d{4}-\d{2}$/.test(raw)) {
      const [year, month] = raw.split("-");
      const dt = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
      if (!Number.isNaN(dt.getTime())) {
        return dt.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
      }
    }
    return "—";
  };

  const getFallbackUnitRateCents = (invoice) => {
    const model = String(invoice?.pricingModel || selectedPricingModel || "").toLowerCase();
    if (model === "fixed_monthly") {
      return Number(selectedRequest?.monthlyBudgetCents || 0);
    }
    return Number(selectedRequest?.hourlyBudgetCents || 0);
  };

  const getInvoiceUnitRateCents = (invoice) => {
    const model = String(invoice?.pricingModel || selectedPricingModel || "").toLowerCase();
    const qty = Number(invoice?.quantity || 0);
    const totalCents = Number(invoice?.subtotalCents || invoice?.amountDueCents || invoice?.totalCents || 0);
    if (model === "hourly" && qty > 0 && totalCents > 0) {
      return Math.round(totalCents / qty);
    }
    if (model === "fixed_monthly" && totalCents > 0) {
      return totalCents;
    }
    return getFallbackUnitRateCents(invoice);
  };

  const formatInvoiceRate = (invoice) => {
    const model = String(invoice?.pricingModel || selectedPricingModel || "").toLowerCase();
    const currency = invoice?.currency || selectedRequest?.currency || "usd";
    const unitRateCents = getInvoiceUnitRateCents(invoice);
    if (!(unitRateCents > 0)) return "—";
    return model === "fixed_monthly"
      ? `${formatCurrency(unitRateCents, currency)}/mo`
      : `${formatCurrency(unitRateCents, currency)}/hr`;
  };

  const getDisplayInvoiceAmountCents = (invoice) => {
    const primary = Number(invoice?.amountDueCents || invoice?.totalCents || 0);
    if (primary > 0) return primary;
    const model = String(invoice?.pricingModel || selectedPricingModel || "").toLowerCase();
    const unitRateCents = getInvoiceUnitRateCents(invoice);
    if (!(unitRateCents > 0)) return 0;
    if (model === "fixed_monthly") return unitRateCents;
    const qty = Number(invoice?.quantity || 0);
    return qty > 0 ? Math.round(unitRateCents * qty) : 0;
  };

  const pricingHistoryCard = selectedRequest ? (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Pricing History
      </Typography>
      {pricingHistoryLoading ? (
        <Typography variant="caption" color="text.secondary">Loading pricing history...</Typography>
      ) : pricingHistoryRows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No pricing history yet.</Typography>
      ) : (
        <Stack spacing={0.9}>
          {pricingHistoryRows.map((entry) => {
            const isMonthly = Number(entry?.toMonthlyCents || 0) > 0 || selectedRequest?.pricingModel === "fixed_monthly";
            const rateLabel = isMonthly
              ? `${formatCurrency(Number(entry?.toMonthlyCents || 0), selectedRequest?.currency || "usd")}/mo`
              : `${formatCurrency(Number(entry?.toHourlyCents || 0), selectedRequest?.currency || "usd")}/hr`;
            const effective = String(entry?.effectiveDate || "").trim();
            const createdSec = Number(entry?.createdAt?.seconds || 0);
            const createdLabel = createdSec > 0 ? new Date(createdSec * 1000).toLocaleDateString() : "—";
            return (
              <Paper key={entry.id} variant="outlined" sx={{ p: 1.1, borderRadius: 1.1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{rateLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {effective ? `Effective ${effective}` : `Recorded ${createdLabel}`}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  ) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2, display: "flex", flexDirection: "column" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 1.25 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200", color: "grey.700" }}>
            <AssignmentIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {showStatusFilter && (
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setRequestPage(0);
              }}
              sx={{ minWidth: 160 }}
            >
              {FILTER_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 7 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={38} />
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">No requests found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedContracts.map((c) => (
                  <TableRow key={c.id} hover onClick={() => openRequestDetails(c)} sx={{ cursor: "pointer", "&:hover": { cursor: "pointer" } }}>
                    <TableCell>{formatDate(c.updatedAt || c.createdAt)}</TableCell>
                    <TableCell>{c.companyName || "—"}</TableCell>
                    <TableCell>{c.roleTitle || c.roleLabel || "—"}</TableCell>
                    <TableCell>
                      <Chip label={statusLabel(c.status)} color={statusColor(c.status)} sx={{ borderRadius: 1 }} />
                    </TableCell>
                    <TableCell align="right">
                      {c.pricingModel === "fixed_monthly"
                        ? `${formatCurrency(c.monthlyBudgetCents || 0, c.currency || "usd")}/mo`
                        : `${formatCurrency(c.hourlyBudgetCents || 0, c.currency || "usd")}/hr`}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {filteredContracts.length > 0 && (
        <Box sx={{ mt: "auto", pt: 0.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={filteredContracts.length}
            page={requestPage}
            onPageChange={(_e, p) => setRequestPage(p)}
            rowsPerPage={requestRowsPerPage}
            onRowsPerPageChange={(e) => {
              setRequestRowsPerPage(parseInt(e.target.value, 10));
              setRequestPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Show"
          />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
        </Box>
      )}

      <Dialog open={promptOpen} onClose={closeReasonPrompt} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {promptAction.type === "status"
            ? `Move Request to ${statusLabel(promptAction.nextStatus)}`
            : promptAction.type === "return" ? "Return Contract to Matching" : "Unclaim Request"}
          <IconButton
            size="small"
            onClick={closeReasonPrompt}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close status dialog"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {promptAction.type === "status" && promptAction.nextStatus === "closed" && (
            <Stack spacing={0.75} sx={{ mb: 1.25 }}>
              {String(promptAction.contract?.status || "").toLowerCase() === "active" && (
                <>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                    This will immediately revoke consultant access.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current contract end date: {formatPromptDate(
                      promptAction.contract?.contractEndAt ||
                        promptAction.contract?.endDate
                    )}
                  </Typography>
                </>
              )}
              {String(promptAction.contract?.status || "").toLowerCase() === "shortlisted" && (
                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                  This will withdraw all outstanding invitations.
                </Typography>
              )}
              <TextField
                label='Type "delete" to confirm'
                size="small"
                fullWidth
                value={promptConfirmText}
                onChange={(e) => setPromptConfirmText(e.target.value)}
              />
            </Stack>
          )}
          {promptAction.type === "status" && promptAction.nextStatus === "active" && (
            <Stack spacing={1.25} sx={{ mb: 1.25 }}>
              <TextField
                label="Contract start date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={promptStartDate}
                onChange={(e) => setPromptStartDate(e.target.value)}
              />
              <TextField
                label="Contract end date (optional)"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={promptEndDate}
                onChange={(e) => setPromptEndDate(e.target.value)}
              />
            </Stack>
          )}
          <TextField
            label={promptAction.type === "return" ? "Return reason" : "Reason (optional)"}
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={promptReason}
            onChange={(e) => setPromptReason(e.target.value)}
          />
          {!!promptError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {promptError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={submitPromptWithGuard}
            disabled={promptBusy}
            startIcon={promptBusy ? <Loader size={16} /> : null}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="bottom"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        PaperProps={{
          sx: {
            top: { xs: 96, sm: 104 },
            left: 0,
            right: 0,
            width: "100%",
            maxWidth: "100%",
            height: { xs: "calc(100dvh - 96px)", sm: "calc(100dvh - 104px)" },
            maxHeight: { xs: "calc(100dvh - 96px)", sm: "calc(100dvh - 104px)" },
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Manage Request
              </Typography>
              {!!selectedRequest && (
                <Typography variant="body2" color="text.secondary">
                  {selectedRequest.roleTitle || selectedRequest.roleLabel || "Untitled role"} • {selectedRequest.companyName || "—"}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setDetailsOpen(false)} aria-label="Close request drawer">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {!selectedRequest ? null : (
          <Box sx={{ p: { xs: 2, md: 3 }, overflowY: "auto", flex: 1, bgcolor: "grey.50" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2.25} alignItems="stretch">
              <Stack spacing={2} sx={{ flex: 1.3 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Status</Typography>
                      <Chip label={statusLabel(selectedRequest.status)} color={statusColor(selectedRequest.status)} sx={{ borderRadius: 1 }} />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle2">Billing</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedRequest.pricingModel === "fixed_monthly"
                          ? `${formatCurrency(selectedRequest.monthlyBudgetCents || 0, selectedRequest.currency || "usd")}/mo`
                          : `${formatCurrency(selectedRequest.hourlyBudgetCents || 0, selectedRequest.currency || "usd")}/hr`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end" sx={{ pt: 0.75, width: "100%" }}>
                        {selectedStatusKey === "matched" && String(selectedRequest?.contractApproval?.status || "").toLowerCase() !== "approved" && (
                          <Button size="small" variant="contained" onClick={() => onApproveMatchedContract?.(selectedRequest)} disabled={!!busyById[selectedRequest.id]}>
                            Approve contract
                          </Button>
                        )}
                        {selectedStatusKey === "matched" && (
                          <Button size="small" variant="outlined" color="warning" onClick={() => openReasonPrompt("return", selectedRequest)} disabled={!!busyById[selectedRequest.id]}>
                            Return to matching
                          </Button>
                        )}
                        {selectedStatusKey === "matched" && String(selectedRequest?.contractApproval?.status || "").toLowerCase() === "approved" && (
                          <Button size="small" variant="contained" color="secondary" onClick={() => onConfirmExternalSignatures?.(selectedRequest)} disabled={!!busyById[selectedRequest.id]}>
                            Confirm external signatures
                          </Button>
                        )}
                        <Tooltip title="Move to active">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openReasonPrompt("status", selectedRequest, "active")}
                              disabled={String(selectedRequest.status || "").toLowerCase() !== "signed" || !!busyById[selectedRequest.id]}
                              sx={actionIconSx(theme, "success")}
                            >
                              <PlayCircleOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Close request">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openReasonPrompt("status", selectedRequest, "closed")}
                              disabled={["matched", "signed", "closed"].includes(String(selectedRequest.status || "").toLowerCase()) || !!busyById[selectedRequest.id]}
                              sx={actionIconSx(theme, "warning")}
                            >
                              <TaskAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    {canRequestRateChange && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openRateDialog}
                        disabled={!!busyById[selectedRequest.id]}
                        sx={{ alignSelf: "flex-end", mt: 0.5 }}
                      >
                        Request Client Rate Change
                      </Button>
                    )}
                  </Stack>
                </Paper>

                {!!upcomingRateChange && (
                  <Alert severity="info">
                    {(() => {
                      const pricingModel = String(upcomingRateChange?.pricingModel || selectedRequest?.pricingModel || "").toLowerCase();
                      const isMonthly = pricingModel === "fixed_monthly";
                      const nowSec = Math.floor(Date.now() / 1000);
                      const effectiveSec = Number(upcomingRateChange?.effectiveFrom?.seconds || upcomingRateChange?.effectiveFrom?._seconds || 0);
                      const isPendingApply = effectiveSec > 0 && effectiveSec <= nowSec;
                      const currentRateLabel = isMonthly
                        ? `${formatCurrency(Number(upcomingRateChange?.currentMonthlyCents || selectedRequest?.monthlyBudgetCents || 0), upcomingRateChange?.currency || selectedRequest?.currency || "usd")}/mo`
                        : `${formatCurrency(Number(upcomingRateChange?.currentHourlyCents || selectedRequest?.hourlyBudgetCents || 0), upcomingRateChange?.currency || selectedRequest?.currency || "usd")}/hr`;
                      const requestedRateLabel = isMonthly
                        ? `${formatCurrency(Number(upcomingRateChange?.requestedMonthlyCents || 0), upcomingRateChange?.currency || selectedRequest?.currency || "usd")}/mo`
                        : `${formatCurrency(Number(upcomingRateChange?.requestedHourlyCents || 0), upcomingRateChange?.currency || selectedRequest?.currency || "usd")}/hr`;
                      const effectiveDate = String(upcomingRateChange?.effectiveDate || "").trim();
                      if (isPendingApply) {
                        return `Pricing change from ${currentRateLabel} to ${requestedRateLabel} is approved and pending application${effectiveDate ? ` (effective ${effectiveDate})` : ""}.`;
                      }
                      return `Pricing will change from ${currentRateLabel} to ${requestedRateLabel} on the effective date${effectiveDate ? ` (${effectiveDate})` : ""}.`;
                    })()}
                  </Alert>
                )}

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {selectedStatusKey === "active" ? "Contractor" : isPostMatchSelected ? "Matched Candidate" : "Shortlist Candidates"}
                  </Typography>
                  {isPostMatchSelected ? (
                    <Stack spacing={1.25}>
                      {!matchedCandidate ? (
                        <Typography variant="body2" color="text.secondary">
                          No matched candidate details available.
                        </Typography>
                      ) : (
                        (() => {
                          const candidateUid = String(matchedCandidate.uid || "");
                          const row = profileBackedCandidates.find((c) => c.uid === candidateUid) || null;
                          const score = Number(row?.score || matchedCandidate.score || matchedCandidate.matchScore || 0);
                          const profitability = row?.profitability || { margin: 0, marginPct: 0 };
                          const name =
                            String(matchedCandidate.displayName || "").trim() ||
                            String(row?.displayName || "").trim() ||
                            "Matched candidate";
                          const feedback =
                            String(matchedCandidate.adminFeedback || "").trim() ||
                            String(matchedCandidate.clientFeedback || "").trim() ||
                            String(row?.profile?.summary || "").trim() ||
                            "No feedback recorded yet.";
                          return (
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.25 }}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {formatServiceLevelSummary(matchedCandidate.service || row?.service, matchedCandidate.level || row?.level)}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip size="small" label={`Match ${score}%`} sx={{ borderRadius: 1 }} />
                                  <Chip size="small" label={`Profitability ${Number(profitability.marginPct || 0).toFixed(1)}%`} sx={{ borderRadius: 1 }} />
                                  <Chip
                                    size="small"
                                    label={`${formatCurrency(Number(profitability.margin || 0), selectedRequest.currency || "usd")}/mo`}
                                    sx={{ borderRadius: 1 }}
                                  />
                                </Stack>
                              </Stack>
                              <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.25, bgcolor: "grey.50" }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                                  Feedback
                                </Typography>
                                <Typography variant="body2">
                                  {feedback}
                                </Typography>
                              </Box>
                            </Paper>
                          );
                        })()
                      )}

                      {selectedStatusKey === "active" && !!matchedCandidate && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.25 }}>
                          <Stack spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Candidate Contract
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Draft agreement between Ops51 and the matched candidate for manual signature workflow.
                            </Typography>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary">
                                Contractor: <span style={{ color: "inherit", fontWeight: 700 }}>{String(matchedCandidate.displayName || "—")}</span>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Start: <span style={{ color: "inherit", fontWeight: 700 }}>
                                  {toDateDisplay(
                                    selectedRequest.contractStartAt ||
                                      selectedRequest.startDate
                                  )}
                                </span>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                End: <span style={{ color: "inherit", fontWeight: 700 }}>
                                  {toDateDisplay(
                                    selectedRequest.contractEndAt ||
                                      selectedRequest.endDate
                                  )}
                                </span>
                              </Typography>
                            </Stack>
                            <Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DescriptionOutlinedIcon fontSize="small" />}
                                onClick={handleDownloadCandidateContractPdf}
                                disabled={contractPdfBusy}
                              >
                                {contractPdfBusy ? "Generating..." : "Download PDF"}
                              </Button>
                            </Box>
                          </Stack>
                        </Paper>
                      )}

                      {selectedStatusKey === "active" && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.25 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Invoices
                              </Typography>
                              {canManageSelected && (
                                <IconButton
                                  size="small"
                                  variant="outlined"
                                  onClick={openClientInvoiceDialog}
                                  disabled={clientInvoicesLoading}
                                  sx={actionIconSx(theme, "success")}
                                >
                                  <AddBoxOutlinedIcon fontSize="small"/>
                                </IconButton>
                                // <Button
                                //   size="small"
                                //   variant="outlined"
                                //   onClick={openClientInvoiceDialog}
                                //   disabled={clientInvoicesLoading}
                                // >
                                //   Create Invoice
                                // </Button>
                              )}
                            </Stack>
                            {!!clientInvoiceError && (
                              <Typography variant="caption" color="error">
                                {clientInvoiceError}
                              </Typography>
                            )}
                            {clientInvoicesLoading ? (
                              <Typography variant="caption" color="text.secondary">
                                Loading invoices...
                              </Typography>
                            ) : clientInvoices.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No client invoices for this contract yet.
                              </Typography>
                            ) : (
                              <TableContainer sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1.25 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Supply Month</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }} align="right">Billable Hours</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {pagedClientInvoices.map((invoice) => {
                                      const status = String(invoice?.status || "").toLowerCase();
                                      const canSend = canManageSelected && ["draft", "open"].includes(status);
                                      const amountCents = getDisplayInvoiceAmountCents(invoice);
                                      const invoiceModel = String(invoice?.pricingModel || selectedPricingModel || "").toLowerCase();
                                      const billableHours = invoiceModel === "hourly" ? Number(invoice?.quantity || 0) : null;
                                      return (
                                        <TableRow key={String(invoice?.id || "")}>
                                          <TableCell>{formatInvoiceDate(invoice?.createdSec)}</TableCell>
                                          <TableCell>{formatSupplyMonth(invoice)}</TableCell>
                                          <TableCell sx={{ maxWidth: 220 }}>
                                            <Tooltip title={String(invoice?.number || invoice?.invoiceId || invoice?.id || "Invoice")}>
                                              <Typography variant="body2" noWrap>
                                                {String(invoice?.number || invoice?.invoiceId || invoice?.id || "Invoice")}
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell align="right">{billableHours == null ? "—" : billableHours}</TableCell>
                                          <TableCell align="right">{formatInvoiceRate(invoice)}</TableCell>
                                          <TableCell>
                                            <Chip size="small" label={statusLabel(status || "draft")} sx={{ borderRadius: 1 }} />
                                          </TableCell>
                                          <TableCell align="right">
                                            {formatCurrency(amountCents, invoice?.currency || selectedRequest.currency || "usd")}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                              <Tooltip title="Open in Stripe">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => window.open(String(invoice?.hostedInvoiceUrl || ""), "_blank", "noopener,noreferrer")}
                                                    disabled={!String(invoice?.hostedInvoiceUrl || "").trim()}
                                                  >
                                                    <OpenInNewIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              <Tooltip title={canSend ? "Send invoice" : "Can send draft/open invoices only"}>
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => sendClientInvoice(invoice?.id || invoice?.invoiceId)}
                                                    disabled={!canSend || clientInvoicesLoading}
                                                  >
                                                    <SendOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            </Stack>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TablePagination
                                        count={clientInvoices.length}
                                        page={clientInvoicePage}
                                        onPageChange={(_event, nextPage) => setClientInvoicePage(nextPage)}
                                        rowsPerPage={clientInvoiceRowsPerPage}
                                        onRowsPerPageChange={(event) => {
                                          setClientInvoiceRowsPerPage(parseInt(event.target.value, 10));
                                          setClientInvoicePage(0);
                                        }}
                                        rowsPerPageOptions={[5, 10, 20]}
                                        colSpan={8}
                                      />
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                                </TableContainer>
                            )}
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  ) : (
                  <>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                      <TextField
                        size="small"
                        label="Search candidates"
                        value={candidateSearch}
                        onChange={(e) => setCandidateSearch(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      {false && <Button
                        variant="outlined"
                        size="small"
                        startIcon={busyById[selectedRequest.id] ? <Loader size={16} /> : <AutoAwesomeOutlinedIcon fontSize="small" />}
                        onClick={generateMatches}
                        disabled={
                          !canManageSelected ||
                          !["submitted", "shortlisted"].includes(String(selectedRequest.status || "").toLowerCase()) ||
                          !!busyById[selectedRequest.id] ||
                          typeof onGenerateCandidateMatches !== "function"
                        }
                      >
                        Generate matches
                      </Button>}
                    </Stack>
                    {profileBackedCandidates.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No candidates available. Only contractors with generated profiles are shown here.
                      </Typography>
                    ) : (
                      <TableContainer sx={{ maxHeight: 420, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1.25 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 60 }}>Pick</TableCell>
                              <TableCell>Candidate</TableCell>
                              <TableCell align="right">Match</TableCell>
                              <TableCell align="right">Profitability</TableCell>
                              <TableCell align="center">Decision</TableCell>
                              <TableCell align="center">Interview</TableCell>
                              <TableCell align="center">Selected</TableCell>
                              <TableCell align="right">Feedback</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedCandidates.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Box sx={{ py: 5, textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary">
                                      No matching candidates.
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              pagedCandidates.map((row) => {
                                const checked = selectedCandidateUids.includes(row.uid);
                                const profitabilityColor = row.profitability.margin < 0 ? "error.main" : "text.secondary";
                                const detail = candidateDetailsMap[row.uid] || {};
                                const contractorDecision = String(detail.contractorDecision || "pending");
                                const interviewStatus = String(detail.interviewStatus || "not_scheduled");
                                const isShortlistedCandidate = checked || Boolean(detail.selected);
                                return (
                                  <TableRow key={row.uid} hover sx={{ opacity: canManageSelected ? 1 : 0.7 }}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        disabled
                                        onChange={() => {
                                          setSelectedCandidateUids((prev) =>
                                            prev.includes(row.uid)
                                              ? prev.filter((id) => id !== row.uid)
                                              : [...prev, row.uid]
                                          );
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {`${row.firstName || ""} ${row.lastName || ""}`.trim() || row.displayName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {row.service} • {row.level} • {row.rateLabel}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {row.score}%
                                      </Typography>
                                      {row.aiMatch && (
                                        <Typography variant="caption" color="primary">
                                          AI match
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="caption" color={profitabilityColor} sx={{ fontWeight: 600 }}>
                                        {row.profitabilityScore > 0
                                          ? `AI ${row.profitabilityScore}/100`
                                          : `${formatCurrency(row.profitability.margin, selectedRequest.currency || "usd")}/mo (${row.profitability.marginPct.toFixed(1)}%)`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        size="small"
                                        label={statusLabel(contractorDecision)}
                                        color={contractorDecision === "accepted" ? "success" : contractorDecision === "declined" ? "error" : "default"}
                                        sx={{ borderRadius: 1 }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        size="small"
                                        label={interviewStatus === "completed" ? "Interviewed" : statusLabel(interviewStatus)}
                                        color={interviewStatusColor(interviewStatus)}
                                        sx={{ borderRadius: 1 }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      {detail.selected ? (
                                        <Chip size="small" label="Yes" color="secondary" sx={{ borderRadius: 1 }} />
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">No</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title={isShortlistedCandidate ? "Update Interview & Feedback" : "Available only for shortlisted candidates"}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled
                                            onClick={() => openCandidateDialog(row.uid)}
                                          >
                                            <RateReviewOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                      <TablePagination
                        component="div"
                        count={filteredCandidates.length}
                        page={candidatePage}
                        onPageChange={(_event, newPage) => setCandidatePage(newPage)}
                        rowsPerPage={candidateRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setCandidateRowsPerPage(parseInt(e.target.value, 10));
                          setCandidatePage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50]}
                        labelRowsPerPage="Show"
                      />
                    </Box>
                  </Stack>
                  {false && <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={saveShortlist}
                        disabled={
                          !canManageSelected ||
                          !["submitted", "shortlisted"].includes(String(selectedRequest.status || "").toLowerCase()) ||
                          !!busyById[selectedRequest.id]
                        }
                      >
                        Save shortlist
                      </Button>
                  </Stack>}
                  </>
                  )}
                </Paper>

                {pricingHistoryCard}
              </Stack>

              {!hideHistoryPanel && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, width: { md: 380, lg: 420 }, alignSelf: "stretch" }}>
                  <Stack spacing={1}>
                    <ButtonGroup size="small" variant="outlined" fullWidth>
                      <Button
                        onClick={() => setHistoryMode("status")}
                        sx={{ bgcolor: historyMode === "status" ? "action.selected" : "transparent" }}
                      >
                        Status History
                      </Button>
                      <Button
                        onClick={() => setHistoryMode("audit")}
                        sx={{ bgcolor: historyMode === "audit" ? "action.selected" : "transparent" }}
                      >
                        Audit History
                      </Button>
                    </ButtonGroup>

                    <Box sx={{ mt: 1, maxHeight: "70vh", overflowY: "auto", pr: 0.5 }}>
                      {historyMode === "status" ? (
                        <Stepper orientation="vertical" nonLinear activeStep={-1}>
                          {(Array.isArray(selectedRequest.statusHistory) ? [...selectedRequest.statusHistory] : [])
                            .sort((a, b) => (b.at?.seconds || 0) - (a.at?.seconds || 0))
                            .map((item, idx) => {
                              const label = statusHistoryLabel(item);
                              return (
                                <Step key={`status-step-${idx}`} expanded>
                                  <StepLabel StepIconComponent={DotStepIcon}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      by {resolveActorLabel(item.by, "system")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {formatDateTime(item.at)}
                                    </Typography>
                                    {!!item.note && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {item.note}
                                      </Typography>
                                    )}
                                  </StepLabel>
                                </Step>
                              );
                            })}
                        </Stepper>
                      ) : requestTimelineLoading ? (
                        <Typography variant="body2" color="text.secondary">Loading timeline...</Typography>
                      ) : requestAuditTimeline.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No audit entries.</Typography>
                      ) : (
                        <Stepper orientation="vertical" nonLinear activeStep={-1}>
                          {requestAuditTimeline.map((entry, idx) => (
                            <Step key={entry.id || idx} expanded>
                              <StepLabel StepIconComponent={DotStepIcon}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {statusLabel(entry.action || "action")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  by {resolveActorLabel(entry.adminUid || entry.by, "system")}
                                  {(() => {
                                    const metaText = formatMetadataTooltip(entry.metadata);
                                    if (!metaText) return null;
                                    return (
                                      <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{metaText}</span>}>
                                        <IconButton
                                          size="small"
                                          sx={{ ml: 0.5, p: 0.25, width: 16, height: 16, verticalAlign: "text-bottom" }}
                                        >
                                          <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  })()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {formatDateTime(entry.createdAt)}
                                </Typography>
                              </StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>

      <Dialog open={rateDialogOpen} onClose={closeRateDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Request Client Rate Change
          <IconButton size="small" onClick={closeRateDialog} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="info">
              This creates a client approval request for this active hire request. If a pending request exists, it will be overwritten.
            </Alert>
            {selectedPricingModel === "fixed_monthly" ? (
              <TextField
                label="Requested monthly rate"
                type="number"
                size="small"
                inputProps={{ min: "0", step: "0.01" }}
                value={requestedMonthlyRate}
                onChange={(e) => setRequestedMonthlyRate(e.target.value)}
                fullWidth
              />
            ) : (
              <TextField
                label="Requested hourly rate"
                type="number"
                size="small"
                inputProps={{ min: "0", step: "0.01" }}
                value={requestedHourlyRate}
                onChange={(e) => setRequestedHourlyRate(e.target.value)}
                fullWidth
              />
            )}
            <TextField
              type="date"
              size="small"
              label="Effective Date"
              value={rateEffectiveDate}
              onChange={(e) => setRateEffectiveDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getFirstDayOfNextMonthString() }}
              helperText={`First day of month, starting ${getFirstDayOfNextMonthString()}.`}
              fullWidth
            />
            <TextField
              label="Reason (optional)"
              value={rateReason}
              onChange={(e) => setRateReason(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            {!!rateDialogError && (
              <Typography variant="caption" color="error">
                {rateDialogError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={submitRateChangeRequest} disabled={rateDialogBusy}>
            {rateDialogBusy ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clientInvoiceDialogOpen} onClose={closeClientInvoiceDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Create Client Invoice
          <IconButton size="small" onClick={closeClientInvoiceDialog} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Contractor Name"
              value={selectedContractorLabel}
              disabled
              fullWidth
            />
            <TextField
              size="small"
              label="Current Contract Price"
              value={selectedContractPriceLabel}
              disabled
              fullWidth
            />
            <TextField
              type="month"
              size="small"
              label="Billing Month"
              value={clientInvoiceMonth}
              onChange={(e) => setClientInvoiceMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Stack spacing={1.25} sx={{ display: 'flex' }}>
              <TextField
                size="small"
                label="Period Start"
                value={resolvedInvoicePeriod.periodStart}
                InputLabelProps={{ shrink: true }}
                disabled
              />
              <TextField
                size="small"
                label="Period End"
                value={resolvedInvoicePeriod.periodEnd}
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Stack>
            <TextField
              label="Billable Hours"
              type="number"
              size="small"
              inputProps={{ min: "1", step: "1", inputMode: "numeric", pattern: "^[1-9][0-9]*$" }}
              value={selectedPricingModel === "fixed_monthly" ? "1" : clientInvoiceQuantity}
              onChange={(e) => {
                if (selectedPricingModel === "fixed_monthly") return;
                const raw = String(e.target.value || "");
                const next = raw.replace(/[^\d]/g, "");
                setClientInvoiceQuantity(next);
              }}
              onKeyDown={(e) => {
                if ([".", "-", "+", "e", "E", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              helperText={selectedPricingModel === "fixed_monthly" ? "Fixed monthly contracts bill as 1 unit." : ""}
              disabled={selectedPricingModel === "fixed_monthly"}
              fullWidth
            />
            <TextField
              size="small"
              label="Total Amount"
              value={formatCurrency(selectedInvoiceTotalCents, selectedRequest?.currency || "usd")}
              helperText={selectedInvoiceTotalHelper}
              disabled
              fullWidth
            />
            {!!clientInvoiceDialogError && (
              <Typography variant="caption" color="error">
                {clientInvoiceDialogError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={submitClientInvoice} disabled={clientInvoiceDialogBusy}>
            {clientInvoiceDialogBusy ? "Creating..." : "Create Invoice"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={candidateDialogOpen} onClose={() => setCandidateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Update Candidate
          <IconButton
            size="small"
            onClick={() => setCandidateDialogOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close update candidate dialog"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {["matched", "signed", "active", "closed"].includes(String(selectedRequest?.status || "").toLowerCase()) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Request is post-matched. Only feedback notes are editable.
            </Typography>
          )}
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <TextField
              select
              size="small"
              label="Interview status"
              value={candidateInterviewStatus}
              onChange={(e) => setCandidateInterviewStatus(e.target.value)}
              disabled={["matched", "signed", "active", "closed"].includes(String(selectedRequest?.status || "").toLowerCase())}
            >
              <MenuItem value="not_scheduled">Not Scheduled</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
              <MenuItem value="availability_sent">Availability Sent</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="no_show">No Show</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="datetime-local"
              label="Interview time (optional)"
              InputLabelProps={{ shrink: true }}
              value={candidateInterviewAt}
              onChange={(e) => setCandidateInterviewAt(e.target.value)}
              disabled={["matched", "signed", "active", "closed"].includes(String(selectedRequest?.status || "").toLowerCase())}
            />
            <TextField
              size="small"
              label="Admin feedback"
              multiline
              minRows={3}
              value={candidateAdminFeedback}
              onChange={(e) => setCandidateAdminFeedback(e.target.value)}
            />
            <TextField
              size="small"
              label="Client feedback"
              multiline
              minRows={3}
              value={candidateClientFeedback}
              onChange={(e) => setCandidateClientFeedback(e.target.value)}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                size="small"
                checked={candidateSelected}
                onChange={(e) => setCandidateSelected(e.target.checked)}
                disabled={["matched", "signed", "active", "closed"].includes(String(selectedRequest?.status || "").toLowerCase())}
              />
              <Typography variant="body2">Mark as selected contractor</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={saveCandidateDetails} disabled={candidateDialogBusy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Contract;
